import axios from "axios";
import { url } from "../../env";

/**
 * Cliente del OTP del registro.
 *
 * El backend manda el código por WhatsApp (plantilla de autenticación de Meta)
 * o por SMS, y el canal lo elige el usuario: no hay fallback automático, así
 * que un fallo de WhatsApp llega con `sugerir_sms` para que la vista ofrezca
 * el otro canal en un clic.
 *
 * Ninguna de estas funciones lanza: todas devuelven `{ ok, ... }`. El flujo de
 * alta ya distingue "no salió el código" de "el alta falló", y una excepción
 * aquí se colaba al catch del registro y salía como el mensaje equivocado.
 */

const CANAL_WHATSAPP = "whatsapp";
const CANAL_SMS = "sms";

export { CANAL_WHATSAPP, CANAL_SMS };

const errorNormalizado = (error, fallback) => {
    if (error.response) {
        const data = error.response.data || {};
        return {
            ok: false,
            // Compatibilidad con el flujo viejo, que leía `response`/`error`.
            response: 0,
            error: true,
            motivo: data.motivo,
            message: data.message || fallback,
            sugerirSms: Boolean(data.sugerir_sms),
            puedeReenviarEn: data.puede_reenviar_en,
            intentosRestantes: data.intentos_restantes,
            register: data.register,
        };
    }

    return {
        ok: false,
        response: 0,
        error: true,
        message: error.request ? "No se pudo conectar con el servidor" : fallback,
    };
};

/**
 * Manda el código. Sin `canal`, decide el servidor
 * (config whatsapp.otp.canal_default): así se cambia de canal sin desplegar
 * el front, que es lo que importa cuando el token de WhatsApp caduca y hay
 * que caerse a SMS ya. La respuesta SIEMPRE dice por dónde salió.
 */
export const sendMessageOtp = async ({ phone, email, canal, reenvio = false }) => {
    try {
        const { data } = await axios.post(
            `${url}/otp/enviar`,
            canal ? { phone, email, canal, reenvio } : { phone, email, reenvio },
            { headers: { "Content-Type": "application/json" } }
        );

        return {
            ok: true,
            response: 1,
            canal: data.canal || canal,
            // Distinto del canal real solo cuando el backend cayó de WhatsApp
            // a SMS: el usuario debe saberlo o se queda esperando un WhatsApp.
            canalSolicitado: data.canal_solicitado,
            telefonoMascarado: data.telefono_mascarado,
            expiraEn: data.expira_en,
            puedeReenviarEn: data.puede_reenviar_en,
        };
    } catch (error) {
        return errorNormalizado(error, "Error al enviar el código");
    }
};

/** Reenvío por el mismo canal (o por el otro, si el usuario lo cambia). */
export const resendOtp = async ({ phone, email, canal }) =>
    sendMessageOtp({ phone, email, canal, reenvio: true });

export const validateOtp = async ({ phone, otp }) => {
    try {
        const { data } = await axios.post(
            `${url}/otp/validar`,
            { phone, otp },
            { headers: { "Content-Type": "application/json" } }
        );

        return { ok: true, message: data.message };
    } catch (error) {
        return errorNormalizado(error, "No pudimos validar el código");
    }
};
